import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';

const PROMO_THRESHOLD = 3000;

const CartDrawer = () => {
  const { items, isOpen, totalCount, totalPrice, removeItem, updateQty, clearCart, closeCart } = useCart();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        closeCart();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, closeCart]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const savings = items.reduce((s, i) => s + ((i.oldPrice ?? i.price) - i.price) * i.quantity, 0);
  const toFreeDelivery = Math.max(0, PROMO_THRESHOLD - totalPrice);
  const deliveryProgress = Math.min(100, (totalPrice / PROMO_THRESHOLD) * 100);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] transition-all duration-400"
        style={{
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: isOpen ? 'blur(4px)' : 'none',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
        onClick={closeCart}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed top-0 right-0 h-full z-[70] flex flex-col transition-transform duration-400"
        style={{
          width: '420px',
          maxWidth: '100vw',
          background: 'rgba(8,8,14,0.98)',
          borderLeft: '1px solid rgba(255,45,120,0.15)',
          backdropFilter: 'blur(20px)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Accent line */}
        <div className="h-0.5 w-full shrink-0" style={{ background: 'linear-gradient(90deg, #ff2d78, #00f5ff, transparent)' }} />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl" style={{ background: 'rgba(255,45,120,0.12)', border: '1px solid rgba(255,45,120,0.25)' }}>
              <i className="ri-shopping-bag-line text-base" style={{ color: '#ff2d78' }} />
            </div>
            <div>
              <h2 className="font-orbitron font-bold text-base text-white">КОРЗИНА</h2>
              <p className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                {totalCount > 0 ? `${totalCount} ${totalCount === 1 ? 'товар' : totalCount < 5 ? 'товара' : 'товаров'}` : 'Пусто'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <button
                onClick={clearCart}
                className="px-3 py-1.5 rounded-lg font-mono text-xs cursor-pointer transition-all duration-200 whitespace-nowrap"
                style={{ color: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.08)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#ff2d78'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.3)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.3)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; }}
              >
                Очистить
              </button>
            )}
            <button
              onClick={closeCart}
              className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-200"
              style={{ color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#fff'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.2)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.4)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; }}
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Free delivery progress */}
        {totalPrice > 0 && (
          <div className="px-6 py-3 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
            {toFreeDelivery > 0 ? (
              <p className="font-mono text-xs mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
                До бесплатной доставки: <span style={{ color: '#39ff14' }}>{toFreeDelivery.toLocaleString()} ₽</span>
              </p>
            ) : (
              <p className="font-mono text-xs mb-2 flex items-center gap-1.5" style={{ color: '#39ff14' }}>
                <i className="ri-truck-line" />
                Бесплатная доставка!
              </p>
            )}
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${deliveryProgress}%`,
                  background: deliveryProgress >= 100
                    ? 'linear-gradient(90deg, #39ff14, #00f5ff)'
                    : 'linear-gradient(90deg, #ff2d78, #ff6b00)',
                  boxShadow: deliveryProgress >= 100 ? '0 0 8px rgba(57,255,20,0.5)' : '0 0 8px rgba(255,45,120,0.4)',
                }}
              />
            </div>
          </div>
        )}

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-6 py-4" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,45,120,0.3) transparent' }}>
          {items.length === 0 ? (
            <EmptyCart onClose={closeCart} />
          ) : (
            <div className="flex flex-col gap-3">
              {items.map(item => (
                <CartItemRow key={item.id} item={item} onRemove={removeItem} onQty={updateQty} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-5 shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {/* Savings */}
            {savings > 0 && (
              <div className="flex items-center justify-between mb-3 px-3 py-2 rounded-xl" style={{ background: 'rgba(57,255,20,0.05)', border: '1px solid rgba(57,255,20,0.12)' }}>
                <span className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Ваша экономия</span>
                <span className="font-mono text-sm font-bold" style={{ color: '#39ff14' }}>-{savings.toLocaleString()} ₽</span>
              </div>
            )}

            {/* Promo code */}
            <PromoInput />

            {/* Total */}
            <div className="flex items-center justify-between mb-4 mt-4">
              <div>
                <p className="font-mono text-xs mb-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>Итого ({totalCount} тов.)</p>
                <p className="font-orbitron font-black text-2xl text-white">{totalPrice.toLocaleString()} ₽</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Доставка</p>
                <p className="font-mono text-sm" style={{ color: totalPrice >= PROMO_THRESHOLD ? '#39ff14' : 'rgba(255,255,255,0.6)' }}>
                  {totalPrice >= PROMO_THRESHOLD ? 'Бесплатно' : 'от 199 ₽'}
                </p>
              </div>
            </div>

            {/* Checkout button */}
            <Link
              to="/checkout"
              onClick={closeCart}
              className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-orbitron font-bold text-sm cursor-pointer transition-all duration-300"
              style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 20px rgba(255,45,120,0.4)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px rgba(255,45,120,0.6)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 20px rgba(255,45,120,0.4)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
            >
              <i className="ri-secure-payment-line text-base" />
              ОФОРМИТЬ ЗАКАЗ
            </Link>

            {/* Trust badges */}
            <div className="flex items-center justify-center gap-4 mt-4">
              {[
                { icon: 'ri-shield-check-line', label: 'Безопасно', color: '#39ff14' },
                { icon: 'ri-truck-line', label: 'Быстро', color: '#00f5ff' },
                { icon: 'ri-refund-2-line', label: 'Возврат', color: '#f0ff00' },
              ].map(b => (
                <div key={b.label} className="flex items-center gap-1.5">
                  <i className={`${b.icon} text-xs`} style={{ color: b.color }} />
                  <span className="font-mono text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/* ── Cart Item Row ── */
const CartItemRow = ({
  item,
  onRemove,
  onQty,
}: {
  item: import('@/hooks/useCart').CartItem;
  onRemove: (id: number) => void;
  onQty: (id: number, qty: number) => void;
}) => (
  <div
    className="flex gap-3 p-3 rounded-xl group transition-all duration-200"
    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${item.color}25`; }}
    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.05)'; }}
  >
    {/* Image */}
    <div
      className="w-16 h-16 rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
      style={{ background: `linear-gradient(135deg, #0a0a14, ${item.color}10)`, border: `1px solid ${item.color}20` }}
    >
      <img
        src={item.image}
        alt={item.name}
        className="w-12 h-12 object-contain"
        style={{ filter: `drop-shadow(0 2px 6px ${item.color}50)` }}
      />
    </div>

    {/* Info */}
    <div className="flex-1 min-w-0">
      <p className="font-orbitron font-bold text-xs text-white leading-snug line-clamp-2 mb-1">{item.name}</p>
      {item.category && (
        <p className="font-mono text-[10px] mb-2" style={{ color: 'rgba(255,255,255,0.3)' }}>{item.category}</p>
      )}

      <div className="flex items-center justify-between">
        {/* Qty controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onQty(item.id, item.quantity - 1)}
            className="w-6 h-6 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-150 font-bold text-sm"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${item.color}20`; (e.currentTarget as HTMLElement).style.color = item.color; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
          >
            <i className="ri-subtract-line text-xs" />
          </button>
          <span className="font-orbitron font-bold text-sm w-6 text-center text-white">{item.quantity}</span>
          <button
            onClick={() => onQty(item.id, item.quantity + 1)}
            className="w-6 h-6 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-150 font-bold text-sm"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${item.color}20`; (e.currentTarget as HTMLElement).style.color = item.color; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
          >
            <i className="ri-add-line text-xs" />
          </button>
        </div>

        {/* Price */}
        <div className="text-right">
          <p className="font-orbitron font-bold text-sm" style={{ color: item.color }}>{(item.price * item.quantity).toLocaleString()} ₽</p>
          {item.oldPrice && item.quantity === 1 && (
            <p className="font-mono text-[10px] line-through" style={{ color: 'rgba(255,255,255,0.25)' }}>{item.oldPrice.toLocaleString()} ₽</p>
          )}
        </div>
      </div>
    </div>

    {/* Remove */}
    <button
      onClick={() => onRemove(item.id)}
      className="w-6 h-6 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200 shrink-0 self-start mt-0.5 opacity-0 group-hover:opacity-100"
      style={{ color: 'rgba(255,255,255,0.3)' }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#ff2d78'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.3)'; }}
    >
      <i className="ri-close-line text-xs" />
    </button>
  </div>
);

/* ── Promo Input ── */
const PromoInput = () => {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const VALID_CODES = ['VIT40', 'HEART30', 'FIRST500', 'FREERUN', 'COLD25', 'SKIN20', 'BONUS2X', 'STOMACH15'];

  const apply = () => {
    if (!code.trim()) return;
    if (VALID_CODES.includes(code.trim().toUpperCase())) {
      setStatus('ok');
    } else {
      setStatus('err');
      setTimeout(() => setStatus('idle'), 2500);
    }
  };

  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Промокод"
          value={code}
          onChange={e => { setCode(e.target.value.toUpperCase()); setStatus('idle'); }}
          onKeyDown={e => e.key === 'Enter' && apply()}
          className="flex-1 px-4 py-2.5 rounded-xl font-mono text-sm text-white outline-none transition-all"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: status === 'ok' ? '1px solid rgba(57,255,20,0.4)' : status === 'err' ? '1px solid rgba(255,45,120,0.4)' : '1px solid rgba(255,255,255,0.08)',
            letterSpacing: '0.1em',
          }}
        />
        <button
          onClick={apply}
          className="whitespace-nowrap px-4 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
          style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,45,120,0.12)'; (e.currentTarget as HTMLElement).style.color = '#ff2d78'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)'; }}
        >
          Применить
        </button>
      </div>
      {status === 'ok' && (
        <p className="font-mono text-xs mt-1.5 flex items-center gap-1" style={{ color: '#39ff14' }}>
          <i className="ri-check-line" /> Промокод применён!
        </p>
      )}
      {status === 'err' && (
        <p className="font-mono text-xs mt-1.5 flex items-center gap-1" style={{ color: '#ff2d78' }}>
          <i className="ri-close-line" /> Промокод не найден
        </p>
      )}
    </div>
  );
};

/* ── Empty State ── */
const EmptyCart = ({ onClose }: { onClose: () => void }) => (
  <div className="flex flex-col items-center justify-center h-full py-16 text-center">
    <div
      className="w-20 h-20 flex items-center justify-center rounded-2xl mb-6"
      style={{ background: 'rgba(255,45,120,0.08)', border: '1px solid rgba(255,45,120,0.15)' }}
    >
      <i className="ri-shopping-bag-line text-3xl" style={{ color: 'rgba(255,45,120,0.4)' }} />
    </div>
    <h3 className="font-orbitron font-bold text-lg text-white mb-2">Корзина пуста</h3>
    <p className="font-mono text-xs mb-8 max-w-[200px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.3)' }}>
      Добавьте лекарства или витамины из каталога
    </p>
    <div className="flex flex-col gap-2 w-full max-w-[200px]">
      <Link
        to="/catalog"
        onClick={onClose}
        className="whitespace-nowrap flex items-center justify-center gap-2 py-3 rounded-xl font-orbitron font-bold text-xs cursor-pointer transition-all duration-300"
        style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 15px rgba(255,45,120,0.35)' }}
      >
        <i className="ri-medicine-bottle-line" />
        В КАТАЛОГ
      </Link>
      <Link
        to="/promos"
        onClick={onClose}
        className="whitespace-nowrap flex items-center justify-center gap-2 py-3 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
        style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
      >
        <i className="ri-price-tag-3-line" />
        Смотреть акции
      </Link>
    </div>
  </div>
);

// Need useState import for PromoInput
import { useState } from 'react';

export default CartDrawer;
